import { z } from "zod";
import { createEndpoint, createEndpointWithNumberedPagination } from "../createEndpoint";
import { HttpClient, QueryParams } from "../httpClient";
import { PublicPageCollectionPagesMetaSchema, PublicPageSchema } from "../schemas/pages";

export interface GetPageByPathQueryParams extends QueryParams {
    path: string;
}

export interface ListCollectionPagesQueryParams extends QueryParams {
    search?: string;
    page?: number;
    per_page?: number;
}

export function createPublicPagesApi(http: HttpClient, baseUrl: string) {
    const publicPagesBaseUrl = `${baseUrl}/public/pages`;
    const publicPageCollectionsBaseUrl = `${baseUrl}/public/page-collections`;

    return {
        /**
         * Get a published page by its path
         * GET /v2/public/pages/by-path?path=/services/development
         */
        getByPath: createEndpoint<
            z.infer<typeof PublicPageSchema>,
            unknown,
            GetPageByPathQueryParams
        >(http, "GET", "/by-path", {
            base: publicPagesBaseUrl,
            responseSchema: PublicPageSchema,
        }),

        /**
         * Get a published page by ID
         * GET /v2/public/pages/:id
         */
        getById: createEndpoint<
            z.infer<typeof PublicPageSchema>,
            unknown,
            QueryParams,
            { id: number }
        >(http, "GET", "/:id", {
            base: publicPagesBaseUrl,
            responseSchema: PublicPageSchema,
            params: ["id"],
        }),

        /**
         * List published pages in a page collection
         * GET /v2/public/page-collections/:slug/pages?search=seo&page=1&per_page=12
         */
        listCollectionPages: createEndpointWithNumberedPagination<
            z.infer<typeof PublicPageSchema>,
            z.infer<typeof PublicPageCollectionPagesMetaSchema>,
            unknown,
            ListCollectionPagesQueryParams,
            { slug: string }
        >(http, "GET", "/:slug/pages", {
            base: publicPageCollectionsBaseUrl,
            itemSchema: PublicPageSchema,
            metaSchema: PublicPageCollectionPagesMetaSchema,
            params: ["slug"],
        }),
    };
}
